/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState } from 'react'
import { FolderOpen, AlertCircle } from 'lucide-react'

interface WorkspaceSetupModalProps {
  onWorkspaceSelected: (dirHandle: FileSystemDirectoryHandle) => void
  onSkip?: () => void
}

export function WorkspaceSetupModal({ onWorkspaceSelected, onSkip }: WorkspaceSetupModalProps) {
  const [error, setError] = useState<string>('')
  const [isSelecting, setIsSelecting] = useState(false)

  const handleSelectWorkspace = async () => {
    setIsSelecting(true)
    setError('')

    try {
      // Request directory access
      // @ts-ignore
      const dirHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents',
      })

      // Verify we have write access
      const testFileName = '.hivemind-test'
      const testFile = await dirHandle.getFileHandle(testFileName, { create: true })
      await dirHandle.removeEntry(testFileName)

      onWorkspaceSelected(dirHandle)
    } catch (err: any) {
      if (err.name === 'AbortError') {
        // User cancelled, that's fine
        setIsSelecting(false)
        return
      }
      console.error('Error selecting workspace:', err)
      setError('Failed to access directory. Please ensure you have write permissions.')
      setIsSelecting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-background-secondary border border-border rounded-lg w-full max-w-xl">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <h2 className="text-2xl font-title mb-2">Welcome to HiveMind</h2>
          <p className="text-sm text-foreground-muted">
            Set up your workspace to get started
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3 p-4 bg-background-tertiary rounded-lg">
            <FolderOpen className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-title text-sm mb-2">Choose Your Workspace Directory</h3>
              <p className="text-xs text-foreground-muted mb-2 leading-relaxed">
                Select a folder where HiveMind will store all your projects. Each project will be created as a subfolder with a <code className="bg-background text-accent px-1 rounded">.hivemind</code> configuration folder.
              </p>
              <p className="text-xs text-foreground-muted leading-relaxed">
                <strong className="text-foreground-secondary">Example:</strong> <code className="bg-background text-accent px-1 rounded">~/Documents/HiveMindProjects/</code>
              </p>
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleSelectWorkspace}
              disabled={isSelecting}
              className="btn-primary w-full py-3 flex items-center justify-center gap-2"
            >
              <FolderOpen className="w-4 h-4" />
              {isSelecting ? 'Opening...' : 'Select Workspace Folder'}
            </button>

            {onSkip && (
              <button
                onClick={onSkip}
                className="btn-ghost w-full py-2 text-sm"
              >
                Skip for now (browser-only mode)
              </button>
            )}
          </div>

          <div className="pt-3 border-t border-border">
            <p className="text-xs text-foreground-muted">
              <strong>Note:</strong> You can change your workspace directory later in Settings.
              Browser-only mode stores projects in IndexedDB (no file system access).
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
